import { injectable, inject } from 'inversify';
import { IChatServerService } from '@/app-modules/server-service';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { ILocalisationService } from '@/app-modules/localisation-service';
import { ISweetAlertService } from '@/app-modules/sweet-alert-service';
import { Lang } from 'vuejs-localization';
import { IEventBusService } from '@/app-modules/event-bus/event-bus-service';
import { CMBanSinPrizeEventName } from '@/components/chat/_domain/modules/chat-context-menu';
import { IChatRoomGameViewModel, IGameConfigurationService, IConfigurationService, ConfigurationPaths } from '@/app-modules/config';
import { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';
import { IPersistedStore } from '@/app-modules/store/persisted-store';
import { User } from '@/app-modules/store/modules/user';
import { base64 } from '@/app-modules/utils';
import { IHttpRequest, IHttpService } from '@/app-modules/http';
import { IUserSearchServiceFactory, IUserSearchService, IUserSearchResult } from '@/components/chat/_domain/modules/user-search-service';
import moment from 'moment';
import { IErrorService } from '@/app-modules/error';
import { ITogglesService } from '@/app-modules/config';

interface ISinBanService {
  sinBinUser(username: string, server: IChatServerService, chatRoom?: string, actionReason?: string): Promise<void>;
  banUser(username: string, server: IChatServerService, chatRoom?: string, actionReason?: string): Promise<void>;
}

interface IUserResult {
  Username: string;
  UsernameMatch?: string;
  CurrentRoom?: IChatRoomGameViewModel;
  OtherRooms?: IChatRoomGameViewModel[];
}

interface IUserSinBan extends IUserResult {
  Reason: string;
  CmUsername: string;
  DateTime: string;
}

@injectable()
class SinBanService implements ISinBanService {
  private readonly $lang: Lang;
  private readonly sinBinUrl = '/sinbin';
  private readonly banUrl = '/ban';

  constructor(
    @inject(SERVICE_IDENTIFIERS.ILocalisationService) private readonly localisationService: ILocalisationService,
    @inject(SERVICE_IDENTIFIERS.ISweetAlertService) private readonly sweetAlertService: ISweetAlertService,
    @inject(SERVICE_IDENTIFIERS.IEventBusService) private readonly eventBusService: IEventBusService,
    @inject(SERVICE_IDENTIFIERS.IConfigurationService) private readonly configurationService: IConfigurationService,
    @inject(SERVICE_IDENTIFIERS.IPersistedStore) private readonly persistedStore: IPersistedStore,
    @inject(SERVICE_IDENTIFIERS.IHttpService) private readonly httpService: IHttpService,
    @inject(SERVICE_IDENTIFIERS.IUserSearchServiceFactory) private readonly userSearchServiceFactory: IUserSearchServiceFactory,
    @inject(SERVICE_IDENTIFIERS.IGameConfigurationService) private readonly gameConfigurationService: IGameConfigurationService,
    @inject(SERVICE_IDENTIFIERS.IErrorService) private readonly errorService: IErrorService,
    @inject(SERVICE_IDENTIFIERS.ITogglesService) private readonly toggleService: ITogglesService
  ) {
    this.$lang = this.localisationService.getLangInstance();
  }

  public async sinBinUser(username: string, server: IChatServerService, chatRoom?: string, actionReason?: string): Promise<void> {
    const user = await this.getUser(username, server, chatRoom);
    const title: string = this.$lang.chat.sin.title;
    const successMessage = this.formatMessageForUser(this.$lang.chat.sin.success, username);
    const failureMessage = this.formatMessageForUser(this.$lang.chat.sin.failed, username);
    const confirmationMessage = this.formatMessageForUser(this.$lang.chat.sin.confirm, username);
    let willSinBinUser = !!actionReason;

    if (!actionReason) {
      const result = await this.showConfirmationMessageForUser(confirmationMessage, title, username);
      willSinBinUser = result.dismiss === undefined;
    }

    if (willSinBinUser) {
      this.sendSinBinCommandForUser(username, server);
      this.eventBusService.getEventBus().$emit(CMBanSinPrizeEventName, this.formatMessageForUser(this.$lang.chat.sin.notification, username));

      if (await this.isSinBanMessageLive()) {
        await this.sinBanMessage(username, user, title, this.$lang.chat.sin.reason, this.sinBinUrl, server, chatRoom, actionReason);
      }
    }
    this.showSuccessOrFailureMessageForUser(willSinBinUser, successMessage, failureMessage, title);
  }

  public async banUser(username: string, server: IChatServerService, chatRoom?: string, actionReason?: string): Promise<void> {
    const user = await this.getUser(username, server, chatRoom);
    const title: string = this.$lang.chat.ban.title;
    const successMessage = this.formatMessageForUser(this.$lang.chat.ban.success, username);
    const failureMessage = this.formatMessageForUser(this.$lang.chat.ban.failed, username);
    const confirmationMessage = this.formatMessageForUser(this.$lang.chat.ban.confirm, username);
    let willBanUser = !!actionReason;

    if (!actionReason) {
      const result = await this.showConfirmationMessageForUser(confirmationMessage, title, username);
      willBanUser = result.dismiss === undefined;
    }

    if (willBanUser) {
      this.sendBanCommandForUser(username, server);
      this.eventBusService.getEventBus().$emit(CMBanSinPrizeEventName, this.formatMessageForUser(this.$lang.chat.ban.notification, username));

      if (await this.isSinBanMessageLive()) {
        await this.sinBanMessage(username, user, title, this.$lang.chat.ban.reason, this.banUrl, server, chatRoom, actionReason);
      }
    }
    this.showSuccessOrFailureMessageForUser(willBanUser, successMessage, failureMessage, title);
  }

  private sendSinBinCommandForUser(username: string, server: IChatServerService) {
    const adminCommand = {
      toPayload() {
        return `<admin command="sinbin" username="${username}" />`;
      }
    };

    server.send(adminCommand);
  }

  private sendBanCommandForUser(username: string, server: IChatServerService) {
    const adminCommand = {
      toPayload() {
        return `<admin command="ban" username="${username}" />`;
      }
    };
    server.send(adminCommand);
  }

  private async showConfirmationMessageForUser(messagePopupText: string, messagePopupTitleText: string, username: string): Promise<SweetAlertResult> {
    // We shouldn't be able to Sin/Ban the JackpotAlert user!
    if (username !== this.$lang.chat.message.jackpotAlert) {
      const refocus = true;
      const sweetAlertProps = await this.getSinBanSweetAlertProperties(messagePopupText, messagePopupTitleText);
      return await this.sweetAlertService.show(sweetAlertProps, refocus);
    }
    return { value: false };
  }

  private async showSinBanReasonMessageForUser(messagePopupText: string, messagePopupTitleText: string, username: string) {
    const refocus = true;
    const sweetAlertProps = this.getSinBanReasonSweetAlertProperties(messagePopupText, messagePopupTitleText);
    return await this.sweetAlertService.show(sweetAlertProps, refocus);
  }

  private showSuccessOrFailureMessageForUser(success: boolean, successMessage: string, failureMessage: string, messagePopupTitleText: string) {
    const refocus = true;
    this.sweetAlertService.show({
      title: messagePopupTitleText,
      text: success ? successMessage : failureMessage,
      type: success ? 'success' : 'error'
    }, refocus);
  }

  private formatMessageForUser(message: string, username: string): string {
    return message.format({ username });
  }

  private async isSinBanMessageLive(): Promise<boolean> {
    const config = await this.configurationService.getConfiguration();
    return config.getConfigurationByPath(ConfigurationPaths.featuretoggle.sin_bin_and_ban_message_islive) === 'true';
  }

  private getSinBanSweetAlertProperties(messagePopupText: string, messagePopupTitleText: string): SweetAlertOptions {
    return Object.assign(
      this.getSinBanSweetAlertBaseProperties(messagePopupText, messagePopupTitleText),
      { showCancelButton: true }
    );
  }

  private getSinBanReasonSweetAlertProperties(messagePopupText: string, messagePopupTitleText: string): SweetAlertOptions {
    return Object.assign(
      this.getSinBanSweetAlertBaseProperties(messagePopupText, messagePopupTitleText),
      {
        allowEscapeKey: false,
        input: 'textarea',
        inputPlaceholder: this.$lang.chat.generic.reasonPlaceholder,
        inputValidator: (value: object) => {
          if (!value) {
            return this.$lang.chat.generic.reasonValidationError;
          }
        }
      }
    );
  }

  private getSinBanSweetAlertBaseProperties(messagePopupText: string, messagePopupTitleText: string): SweetAlertOptions {
    return {
      title: messagePopupTitleText,
      text: messagePopupText,
      type: 'warning',
      allowOutsideClick: false,
      focusConfirm: false,
      showConfirmButton: true
    };
  }

  private async sendSinBanToMailGroup(request: IUserSinBan, url: string) {
    try {
      const cmUser = this.persistedStore.Get(User);
      const httpRequest = this.getHttpRequestMailGroupRequest(request, cmUser, url);
      const useNewEndPoint = await this.toggleService.isLive('CIC_318_UseNETSinBanEndPoint');
      const response = await this.httpService.post(httpRequest, useNewEndPoint);
      if (useNewEndPoint) {
        return (response.status === 200) ? true : false;
      }
      else {
        return (response.status === 200 && response.data.Status === 200) ? true : false;
      }
    } catch {
      return false;
    }
  }

  private async sinBanMessage(username: string, user: IUserResult, messagePopupTitleText: string, reason: string, url: string, server: IChatServerService, chatRoom?: string, actionReason?: string): Promise<void> {
    if (!actionReason) {
      const reasonConfirmationMessage = this.formatMessageForUser(reason, username);
      const reasonResult = await this.showSinBanReasonMessageForUser(reasonConfirmationMessage, messagePopupTitleText, username);
      actionReason = reasonResult.value;
    }
    if (await this.sendSinBanToMailGroup(this.buildSinBanReasonRequest(username, user, actionReason, chatRoom), url) !== true) {
      this.errorService.logError(new Error(`Failed to send email request for sinban: ${username}`));
    }
  }

  private buildSinBanReasonRequest(username: string, user: IUserResult, reason: string, chatRoom?: string): IUserSinBan {
    const cmUser = this.persistedStore.Get(User);
    const request = {Username: username, Reason: reason, CmUsername: cmUser.username, DateTime: moment().format('DD-MM-YYYY HH:mm:ss')};
    if (user) {
      Object.assign(request, user);
    }
    return request;
  }

  private getHttpRequestMailGroupRequest(request: object, cmUser: User, url: string): IHttpRequest {
    const encodedAuth = base64.Encode(`${cmUser.userId}:${cmUser.crosscheckId}`);
    const httpRequest: IHttpRequest = {
      Url: url,
      XChatModsAuthorization: encodedAuth,
      Payload: request
    };
    return httpRequest;
  }

  private async getAdditionalUserData(username: string, server: IChatServerService): Promise<IUserSearchResult[]> {
    let userSearchService: IUserSearchService = null;
    try {
      userSearchService = this.userSearchServiceFactory.getUserSearchService(server);
      const results = await userSearchService.findUserByUsername(username);
      if (!results || results.length === 0) {
        this.errorService.logInfo(`Failed to retrieve user from DB: ${username} for sinban`, {username});
        return [];
      }

      const filteredResults = this.filterUsersByUsername(results, username);

      if (filteredResults.length !== 0) {
        return filteredResults;
      } else {
        this.errorService.logInfo('Username retrieved from DB: ' + results[0].username +
        ' does not match selected username: ' + username + ' in the sinban message',
        {username, results});
        return results;
      }
    }
    finally {
      if (userSearchService) {
        userSearchService.dispose();
      }
    }
  }

  private async getCurrentRoomData(chatRoom: string): Promise<IChatRoomGameViewModel> {
    const chatRoomList = await this.gameConfigurationService.getAllChatooms();
    const definedRoomResults = chatRoomList.filter((room) => room.RoomName === chatRoom);
    return (definedRoomResults.length !== 0) ? definedRoomResults[0] : null;
  }

  private async getAdditionalRoomData(user: IUserResult, userData: IUserSearchResult[]): Promise<IChatRoomGameViewModel[]> {
    const usernameRoomResults = this.filterUsersByUsername(userData, user.Username);
    return this.SearchResultsToChatRoomModels((!user.CurrentRoom) ? usernameRoomResults : usernameRoomResults.filter((res) => res.roomId !== user.CurrentRoom.Id));
  }

  private filterUsersByUsername(results: IUserSearchResult[], username: string): IUserSearchResult[]{
    return results.filter((res) => this.compareString(res.username, username) );
  }

  private async getUser(username: string, server: IChatServerService, chatRoom?: string): Promise<IUserResult> {
    const searchResults = await this.getAdditionalUserData(username, server);
    const user: IUserResult = {Username: username};
    if (searchResults.length !== 0) {
      user.UsernameMatch = searchResults[0].username;
    }
    if (chatRoom) {
      user.CurrentRoom = await this.getCurrentRoomData(chatRoom);
    }
    user.OtherRooms = await this.getAdditionalRoomData(user, searchResults);

    return user;
  }

  private compareString(str1: string, str2: string): boolean {
    return str1.toLowerCase() === str2.toLowerCase();
  }

  private SearchResultToChatRoomModel(result: IUserSearchResult): IChatRoomGameViewModel {
    return {Id: result.roomId, RoomName: result.roomName, GameName: result.gameName};
  }

  private SearchResultsToChatRoomModels(results: IUserSearchResult[]): IChatRoomGameViewModel[] {
    const chatRooms: IChatRoomGameViewModel[] = [];
    results.forEach((res) => chatRooms.push(this.SearchResultToChatRoomModel(res)));
    return chatRooms;
  }
}

export { ISinBanService, SinBanService };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/chat/_domain/modules/sin-ban-service/sin-ban-service.ts